import { useCallback, useEffect, useRef, useState } from "react";

import SpeakerCardFrontView from "./SpeakerCardFrontView";
import SpeakerCardBackView from "./SpeakerCardBackView";
import { CaretLeft, CaretRight } from "phosphor-react";
import { SPEAKERS } from "../constants/speakers";

export default function ResponsiveSpeakerCarousel({
  openModal,
  isModalOpen = false,
}) {
  const containerRef = useRef(null);
  const cardRefs = useRef([]);
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cols, setCols] = useState(1);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const AUTOPLAY_INTERVAL = 5000;

  // Auto-scroll logic
  useEffect(() => {
    if (!isAutoPlaying || isModalOpen || slides.length === 0) return;
    const interval = setInterval(() => {
      scrollToSlide((currentSlide + 1) % slides.length);
    }, AUTOPLAY_INTERVAL);
    return () => clearInterval(interval);
  }, [isAutoPlaying, isModalOpen, slides.length, currentSlide]);

  // When currentSlide changes, scroll to it
  useEffect(() => {
    if (slides.length > 0) {
      scrollToSlide(currentSlide);
    }
    // eslint-disable-next-line
  }, [currentSlide, slides.length]);

  // Pause/resume autoplay on mouse events
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => {
    if (!isModalOpen) setIsAutoPlaying(true);
  };

  // Helper: get number of columns from computed grid

  const calculateColumns = useCallback(() => {
    if (!containerRef.current || cardRefs.current.length === 0) return 1;

    const firstCard = cardRefs.current[0];
    if (!firstCard) return 1;

    const containerRect = containerRef.current.getBoundingClientRect();
    const cardRect = firstCard.getBoundingClientRect();

    const estimatedCols = Math.round(containerRect.width / cardRect.width);
    return Math.max(1, estimatedCols);
  }, []);

  // Group speakers into slides (each slide = 1 row = `cols` cards)
  const updateSlides = useCallback(() => {
    const newCols = calculateColumns();
    setCols(newCols);

    const newSlides = [];
    for (let i = 0; i < SPEAKERS.length; i += newCols) {
      const indices = [];
      for (let j = 0; j < newCols && i + j < SPEAKERS.length; j++) {
        indices.push(i + j);
      }
      newSlides.push(indices);
    }
    setSlides(newSlides);
    setCurrentSlide(0);
  }, [calculateColumns]);

  // Handle resize
  useEffect(() => {
    updateSlides();
    const resizeObserver = new ResizeObserver(updateSlides);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [updateSlides]);

  const scrollToSlide = (index) => {
    if (!containerRef.current) return;
    const slideWidth = containerRef.current.scrollWidth / slides.length;
    containerRef.current.scrollTo({
      left: slideWidth * index,
      behavior: "smooth",
    });
    // setCurrentSlide(index);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || slides.length <= 1) return;

    const updateCurrentSlide = () => {
      const { scrollLeft, scrollWidth } = container;
      // Calculate the closest slide index
      const index = Math.ceil((scrollLeft / scrollWidth) * (slides.length - 1));
      setCurrentSlide(index);
    };

    // Sync on mount (in case of initial layout)
    updateCurrentSlide();

    // Listen to scroll
    container.addEventListener("scroll", updateCurrentSlide, { passive: true });
    return () => container.removeEventListener("scroll", updateCurrentSlide);
  }, [slides.length]);

  return (
    <div className="relative w-full">
      {/* Horizontal Scroll Container */}
      <div
        ref={containerRef}
        // onScroll={handleScroll}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="flex overflow-x-auto scroll-smooth scrollbar-hide gap-4"
        style={{ scrollSnapType: "x mandatory" }}
      >
        {slides.map((indices, slideIndex) => (
          <div
            key={slideIndex}
            className="snap-start flex-shrink-0"
            style={{ width: "100%" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8 w-full">
              {indices.map((speakerIndex) => {
                const speaker = SPEAKERS[speakerIndex];
                return (
                  <div
                    key={speaker.id}
                    ref={(el) => (cardRefs.current[speakerIndex] = el)}
                    className="group cursor-pointer transform"
                  >
                    <div className="bg-white rounded-none overflow-hidden">
                      <div className="relative overflow-hidden aspect-[300/386] md:aspect-[286/350]">
                        <div
                          className="relative w-full h-full transition-transform duration-700 group-hover:[transform:rotateY(180deg)]"
                          style={{
                            perspective: "1000px",
                            transformStyle: "preserve-3d",
                          }}
                        >
                          <SpeakerCardFrontView speaker={speaker} />
                          <SpeakerCardBackView
                            speaker={speaker}
                            onViewBio={openModal}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* NAVIGATION */}
      <div className="flex justify-center mt-6 space-x-4 items-center">
        <button
          className="p-2 bg-[#CBF07F] text-[#11302D] disabled:opacity-30"
          aria-label="Scroll Left"
          disabled={currentSlide === 0}
          onClick={() => scrollToSlide(Math.max(0, currentSlide - 1))}
          type="button"
        >
          <CaretLeft size={24} weight="bold" />
        </button>

        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollToSlide(i)}
            className={`w-3 h-3 rounded-full hidden md:block ${
              i === currentSlide ? "bg-[#11302D]" : "bg-[#d9d9d9]"
            }`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}

        <button
          className="p-2 bg-[#CBF07F] text-[#11302D] disabled:opacity-30"
          aria-label="Scroll Right"
          disabled={currentSlide === slides.length - 1}
          onClick={() => {
            if (slides.length === 0) return;
            const next = (currentSlide + 1) % slides.length;
            scrollToSlide(next);
            return;
          }}
          type="button"
        >
          <CaretRight size={24} weight="bold" />
        </button>
      </div>
    </div>
  );
}
